import type {
	ApiGetAiTokenUsageTimeseriesQueryParams,
	ApiGetComputeCostTimeseriesQueryParams,
} from '@repo/api-codegen';
import type { IIntegration } from '../../api';
import type { DateRange } from '../../components/DateRangeButtonGroup/DateRangeButtonGroup';
export type FilterType = 'all' | 'owned' | 'subscribed';
export type IntegrationFilterType = 'all' | IIntegration['name'];
export type RequestFilterType = 'all' | 'created' | 'assigned';
export type QuestionFilterType = 'all' | 'created' | 'assigned';
export type AiTokenUsageFilterType = 'all' | 'user' | 'user_role' | 'ai_model';

export type AiGroupByType = NonNullable<
	ApiGetAiTokenUsageTimeseriesQueryParams['group_by']
>;

export type AiDisplayAsType = 'tokens' | 'credits';

export type TimeRange = NonNullable<
	ApiGetAiTokenUsageTimeseriesQueryParams['time_range']
>;

export type ViewOption = NonNullable<
	ApiGetAiTokenUsageTimeseriesQueryParams['view_option']
>;

export const isValidTimeRange = (value: string): value is TimeRange =>
	['24h', '7d', '30d', '90d', '180d', '365d', 'all'].includes(value);

export const isValidViewOption = (value: string): value is ViewOption =>
	['day', 'week', 'month', 'year'].includes(value);

export type TimeRangeValue = {
	timeRange: string;
	dateRange: DateRange;
};
export interface ChartDataPoint {
	date: Date;
	Normal?: number;
	Error?: number;
	'Open incidents'?: number;
	'Acknowledged incidents'?: number;
}

export interface IntegrationChartDataPoint {
	date: Date;
	completedSync: number;
	failedSync: number;
}

export interface AutomationChartDataPoint {
	date: Date;
	totalAutomations: number;
	totalTasksAutomated: number;
}
export interface PolicyChartDataPoint {
	date: Date;
	ok: number;
	needsRemediation: number;
}
export interface RequestsChartDataPoint {
	date: Date;
	Open: number;
	Active: number;
	Denied: number;
	Closed: number;
}

export interface QuestionsChartDataPoint {
	date: Date;
	Open: number;
	InProgress: number;
	InReview: number;
	Answered: number;
	Cancelled: number;
}

export type FilterOption = {
	label: string;
	value: string;
};

export type FilterOptions = FilterOption[][];

export interface MetricsOverviewTabProps {
	filterTypes: string[];
	setFilterTypes: (filterTypes: string[]) => void;
	filterOptions: FilterOptions;
}

export interface OverviewMetric {
	title: string;
	value: string;
	icon?: React.ReactNode;
	color?: string;
}

export type Channel = {
	label: string;
	color: string;
	x:
		| keyof ChartDataPoint
		| keyof IntegrationChartDataPoint
		| keyof PolicyChartDataPoint
		| keyof AutomationChartDataPoint
		| keyof RequestsChartDataPoint;
	y:
		| keyof ChartDataPoint
		| keyof IntegrationChartDataPoint
		| keyof PolicyChartDataPoint
		| keyof AutomationChartDataPoint
		| keyof RequestsChartDataPoint;
	renderArea: boolean;
	areaOpacity: number;
	zIndex: number;
};

export type RequestMetricCardType = 'Open' | 'Active' | 'Denied' | 'Closed';
export type IntegrationMetricCardType = 'Completed Sync' | 'Failed';
export type MonitorMetricCardType =
	| 'Normal'
	| 'Error'
	| 'Open Incidents'
	| 'Acknowledged Incidents';
export type PolicyMetricCardType = 'Normal' | 'Error';
export type QuestionMetricCardType =
	| 'Open'
	| 'InProgress'
	| 'InReview'
	| 'Answered'
	| 'Cancelled';

export type AiTokenUsageMetricCardType =
	| 'Total Requests'
	| 'Input Tokens'
	| 'Output Tokens'
	| 'Total Tokens'
	| 'Cost'
	| 'Cache Creation Input Tokens'
	| 'Cache Read Input Tokens';

export type AiCostUsageMetricCardType =
	| 'Total Requests'
	| 'Input Cost'
	| 'Output Cost'
	| 'Total Cost'
	| 'Cache Creation Cost'
	| 'Cache Read Cost';

export type ComputeCostMetricCardType =
	| 'total_cost'
	| 'total_requests'
	| 'vcpu_cost'
	| 'memory_cost'
	| 'avg_duration'
	| 'success_rate'
	| 'unique_jobs'
	| 'avg_vcpu';

export type TimeRangeType = NonNullable<
	ApiGetComputeCostTimeseriesQueryParams['time_range']
>;
export type ViewOptionType = NonNullable<
	ApiGetComputeCostTimeseriesQueryParams['view_option']
>;

export type MetricCardType =
	| RequestMetricCardType
	| IntegrationMetricCardType
	| MonitorMetricCardType
	| PolicyMetricCardType
	| QuestionMetricCardType
	| AiTokenUsageMetricCardType
	| AiCostUsageMetricCardType
	| ComputeCostMetricCardType;

export type MonitorsPageTab = 'monitors' | 'incidents' | 'overview';

export type IntegrationsOverviewTabControllerProps = {
	onMetricCardClick?: (
		metricType: IntegrationMetricCardType | MetricCardType
	) => void;
	integrations: IIntegration[];
};

export interface GenericOverviewTabControllerProps {
	onMetricCardClick?: (metricType: MetricCardType) => void;
}
export type PoliciesOverviewTabControllerProps = {
	onMetricCardClick?: (metricType: MetricCardType) => void;
};
